'use client'

import { useEffect, useRef } from 'react'
import { storyblokEditable, renderRichText } from '@storyblok/react'
import { TimelineStoryblok, TimestampStoryblok } from '@/component-types-sb'

export default function Timeline({ blok }: { blok: TimelineStoryblok }) {
    const containerRef = useRef<HTMLDivElement>(null)
    const center = blok.alignement === 'center'
    const animated = blok.animated !== false

    useEffect(() => {
        if (!animated) return
        const elements = containerRef.current?.querySelectorAll('.timestamp-item')
        if (!elements) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in')
                        observer.unobserve(entry.target)
                    }
                })
            },
            { threshold: 0.4 }
        )

        elements.forEach((el) => observer.observe(el))
        return () => observer.disconnect()
    }, [blok.timestamps, animated])

    return (
        <section {...storyblokEditable(blok)} className="timeline max-w-[1200px] mx-auto p-5 md:p-10">
            {blok.heading && <h2 className="mb-8">{blok.heading}</h2>}
            <div ref={containerRef} className={`relative ml-2 border-l-2 border-primary ${center ? 'md:border-l-0 md:ml-0' : ''}`}>
                {/* Center line (desktop only) */}
                {center && <div className="hidden md:block absolute top-7 bottom-0 left-1/2 w-[2px] bg-primary -translate-x-1/2" />}

                {blok.timestamps?.map((item: TimestampStoryblok, index: number) => {
                    const textHtml = item.text
                        ? String(renderRichText(item.text as Parameters<typeof renderRichText>[0]) || '')
                        : ''
                    const isRight = center && index % 2 === 1
                    const bg = blok.backgroundColor === 'gray' ? 'bg-background-gray-light'
                        : blok.backgroundColor === 'alternately' ? (index % 2 === 0 ? 'bg-white' : 'bg-background-gray-light')
                        : 'bg-white'

                    return (
                        <div key={item._uid} {...storyblokEditable(item)} className={`timestamp-item ${animated ? 'opacity-0 translate-y-8 transition-all duration-700 ease-out [&.animate-in]:opacity-100 [&.animate-in]:translate-y-0' : ''} relative pl-8 pb-10 last:pb-0 ${center ? `md:w-1/2 md:pl-0 ${isRight ? 'md:ml-auto md:pl-10' : 'md:pr-10'}` : ''}`}>
                            <div className={`absolute w-4 h-4 bg-primary rounded-full border-2 border-white top-[26px] -left-[11px] ${center ? (isRight ? 'md:-left-[10px]' : 'md:left-auto md:-right-[10px]') : ''}`} />
                            <div className={`border border-background-gray p-4 ${bg}`}>
                                {item.timestamp && (
                                    <p className="font-bold text-primary mb-1">{item.timestamp}</p>
                                )}
                                {textHtml && (
                                    <div className="prose max-w-none [&>*:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: textHtml }} />
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </section>
    )
}
